/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IFarmlandsProductDescriptionData } from './farmlands-product-description.data';
import { IFarmlandsProductDescriptionProps } from './farmlands-product-description.props.autogenerated';
import { getExtensionProperty, ePropsValue } from '../../common/method/functions';

export interface IProductDescriptionViewProps extends IFarmlandsProductDescriptionProps<{}> {
    ProductDescription: IModuleProps;
    ProductDescriptionNode: React.ReactNode;
}

export interface IFarmlandsProductDescriptionState {
    onlineLongDescription: string;
    onlineShortDescription: string;
}

/**
 *
 * ProductSpecification component
 * @extends {React.Component<IFarmlandsProductDescriptionProps<IFarmlandsProductDescriptionData>>}
 */
@observer
class ProductDescription extends React.Component<IFarmlandsProductDescriptionProps<IFarmlandsProductDescriptionData>, IFarmlandsProductDescriptionState> {
    constructor(props: IFarmlandsProductDescriptionProps<IFarmlandsProductDescriptionData>) {
        super(props);
        this.state = {
            onlineLongDescription: '',
            onlineShortDescription: '',
        };
    }

    public componentDidUpdate(): void {
        const {
            ExtensionProperties: eProps
        } = this.props.data.product.result!;

        // Only update description values if the new values are not empty and are different from the existing strings.
        const longDesc = getExtensionProperty(eProps!, 'OnlineLongDescription', ePropsValue.String);
        const shortDesc = getExtensionProperty(eProps!, 'OnlineShortDescription', ePropsValue.String);

        if (this.state.onlineLongDescription !== longDesc && longDesc !== '') {
            this.setState({
                onlineLongDescription: longDesc
            });
        }

        if (this.state.onlineShortDescription !== shortDesc && shortDesc !== '') {
            this.setState({
                onlineShortDescription: shortDesc
            });
        }
    }

    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            ProductDescription: {
                moduleProps: this.props,
                className: classnames('ms-product-specification', this.props.config.className)
            },
            ProductDescriptionNode: (
                <RichTextComponent
                    className='ms-buybox__long-description'
                    text={this.state.onlineLongDescription || this.state.onlineShortDescription}
                />
            )
        } as IProductDescriptionViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }
}

export default ProductDescription;
